import { useState } from "react";
import { Layout } from "../components/Layout.js";
import { Button } from "../components/Button.js";
import { useScreener } from "../state.js";
import { useAudio } from "../audio/AudioProvider.js";
import { useFlow } from "./useFlow.js";

type Side = "left" | "right";

export function Headphones() {
  const { config } = useScreener();
  const { ensureEngine } = useAudio();
  const { go } = useFlow();
  const [confirmedHeadphones, setConfirmedHeadphones] = useState(false);
  const [heard, setHeard] = useState<Record<Side, boolean>>({
    left: false,
    right: false,
  });
  const [playing, setPlaying] = useState<Side | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function play(side: Side) {
    if (!config) return;
    setError(null);
    try {
      const engine = await ensureEngine(config.audio, config.din);
      setPlaying(side);
      await engine.playChannelCheck(side);
      setHeard((h) => ({ ...h, [side]: true }));
    } catch {
      setError("We couldn't play audio. Check your device volume and try again.");
    } finally {
      setPlaying(null);
    }
  }

  const canContinue = confirmedHeadphones && heard.left && heard.right;

  return (
    <Layout step="headphones">
      <div className="screen-illustration" aria-hidden>
        <HeadphonesIllustration />
      </div>
      <h2 className="center">Put on your headphones</h2>
      <p className="muted center screen-copy">
        Find a quiet place and put on your headphones — wired ones give the most
        reliable result. Make sure they are the right way round, then play a
        sound in each ear to check.
      </p>

      <div className="audio-check-panel">
        <p className="audio-check-label">Check sound in each ear</p>
        <div className="pill-row">
          <button
            className={"pill" + (heard.left ? " active" : "")}
            onClick={() => play("left")}
            disabled={playing !== null}
          >
            {playing === "left" ? "Playing…" : heard.left ? "✓ Left ear checked" : "▶ Play left ear"}
          </button>
          <button
            className={"pill" + (heard.right ? " active" : "")}
            onClick={() => play("right")}
            disabled={playing !== null}
          >
            {playing === "right"
              ? "Playing…"
              : heard.right
                ? "✓ Right ear checked"
                : "▶ Play right ear"}
          </button>
        </div>
      </div>

      <label className="option center-option">
        <input
          type="checkbox"
          checked={confirmedHeadphones}
          onChange={(e) => setConfirmedHeadphones(e.target.checked)}
        />
        I'm wearing headphones and heard both sides correctly.
      </label>

      {error ? <div className="note">{error}</div> : null}

      <div className="actions">
        <Button variant="secondary" onClick={() => go("welcome")}>
          Back
        </Button>
        <Button disabled={!canContinue} onClick={() => go("set-volume")}>
          Continue
        </Button>
      </div>
    </Layout>
  );
}

function HeadphonesIllustration() {
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" fill="none">
      <circle cx="48" cy="48" r="44" fill="var(--primary-soft)" />
      <path
        d="M26 58v-8c0-12.15 9.85-22 22-22s22 9.85 22 22v8"
        stroke="var(--primary-color)"
        strokeWidth="5"
        strokeLinecap="round"
        fill="none"
      />
      <rect x="20" y="54" width="12" height="20" rx="5" fill="var(--primary-color)" />
      <rect x="64" y="54" width="12" height="20" rx="5" fill="var(--primary-color)" />
    </svg>
  );
}
